import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'

export interface AccessGrant {
  id: number
  actor_id: number
  actor_kind: 'user' | 'role' | 'organisation' | 'partnership'
  label: string
  permission: 'view' | 'edit'
}

export interface ActorSearchResult {
  actor_id: number
  actor_kind: 'user' | 'role' | 'organisation' | 'partnership'
  label: string
}

export function useAccessGrants(resourceType: string, objectId: number | null) {
  return useQuery<AccessGrant[]>({
    queryKey: ['acl-grants', resourceType, objectId],
    queryFn: () =>
      api
        .get('/auth/acl/grants/', {
          params: { resource_type: resourceType, object_id: objectId },
        })
        .then((r) => r.data),
    enabled: !!objectId,
  })
}

export function useGrantAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      resource_type: string
      object_id: number
      actor_id: number
      permission: string
    }) => api.post('/auth/acl/grants/', data).then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ['acl-grants', variables.resource_type, variables.object_id],
      })
    },
  })
}

export function useRevokeAccess() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      resource_type: string
      object_id: number
      actor_id: number
      permission: string
    }) => api.delete('/auth/acl/grants/', { data }).then((r) => r.data),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({
        queryKey: ['acl-grants', variables.resource_type, variables.object_id],
      })
    },
  })
}

export function useActorSearch(query: string) {
  return useQuery<ActorSearchResult[]>({
    queryKey: ['acl-actor-search', query],
    queryFn: () =>
      api.get('/auth/acl/actors/search/', { params: { q: query } }).then((r) => r.data),
    enabled: query.length >= 2,
    staleTime: 30_000,
  })
}
